import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { useTableParams } from '../../core/hooks/table-params'
import { IColumn, Table } from '../../core/components/Table'
import { Pagination } from '../../core/components/Pagination'
import Loader from '../../core/components/Loader'
import { SimpleSearchPaginationParams } from '../../core/services/utils'
import { IBlogCategory, INameId } from '../../core/interfaces'
import {
    getAllBlogCategories,
    createBlogCategory,
    updateBlogCategory,
    deleteBlogCategory,
    toggleBlogCategoryVisibility,
} from '../../services/blog-categories'
import { INameModal } from '../helpers/INameModal'

export const BlogCategoryListPage: React.FC = (): JSX.Element => {
    const { page, setPage, size, setSize, totalElements, setTotalElements, sortBy, setSortBy } =
        useTableParams({ defaultSortBy: 'createdAt' })

    const [loading, setLoading] = useState(false)
    const [categories, setCategories] = useState<IBlogCategory[]>([])
    const [isCheck, setIsCheck] = useState<string[]>([])
    const [editMode, setEditMode] = useState(false)
    const [currentCategory, setCurrentCategory] = useState<INameId | undefined>()
    const [showModal, setShowModal] = useState(false)
    const searchParams = useRef<{ [key: string]: string }>({})
    const timer = useRef<NodeJS.Timeout | null>(null)

    const getData = useCallback(async () => {
        setLoading(true)
        try {
            const params: SimpleSearchPaginationParams = { page, size, sortBy }
            params.text = searchParams.current.name
            const response = await getAllBlogCategories(params)
            setTotalElements(response.data.totalElements)
            setCategories(response.data.content)
            setPage(page + 1)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy])

    useEffect(() => {
        getData()
    }, [getData, page, size, sortBy])

    const handleSortBy = (sort: string) => {
        setSortBy(sort === sortBy ? 'createdAt' : sort)
    }

    const handleSearchParams = useCallback(
        async (col: IColumn, q: string) => {
            if (timer.current) clearTimeout(timer.current)
            searchParams.current = { ...searchParams.current, [col.accessor]: q }
            timer.current = setTimeout(async () => {
                setLoading(true)
                try {
                    const params: SimpleSearchPaginationParams = { page: 0, size, sortBy }
                    params.text = searchParams.current.name
                    const { data, status } = await getAllBlogCategories(params)
                    if (status === 200) {
                        setCategories(data.content)
                        setTotalElements(data.totalElements)
                        setSize(data.size)
                        setPage(data.pageNumber + 1)
                    }
                } catch (err) {
                    console.error(err)
                } finally {
                    setLoading(false)
                }
            }, 2000)
        },
        [page, size, sortBy],
    )

    const handleToggle = (id: string) => {
        toggleBlogCategoryVisibility(id as any).then(() => window.location.reload())
    }

    const columns: IColumn[] = [
        { title: 'ID', accessor: 'id', sortable: true },
        { title: 'Name', accessor: 'name', sortable: true, searchable: true },
        { title: 'Slug', accessor: 'slug', sortable: false },
        {
            title: 'Visible',
            accessor: '',
            render: (row) => <span>{row.visible ? 'Yes' : 'No'}</span>,
        },
        {
            title: 'Edit',
            accessor: '',
            render: (row) => (
                <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        setEditMode(true)
                        setCurrentCategory({ id: row.id, name: row.name })
                        setShowModal(true)
                    }}
                >
                    Edit
                </div>
            ),
        },
        {
            title: 'Actions',
            accessor: '',
            render: (row) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleToggle(row.id)}
                >
                    {row.visible ? 'Hide' : 'Show'}
                </Button>
            ),
        },
    ]

    return (
        <div style={{ padding: 20, backgroundColor: '#F8F8F8', position: 'relative' }}>
            {loading && <Loader />}
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 20,
                }}
            >
                <h2>Blog Categories</h2>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                        setEditMode(false)
                        setCurrentCategory(undefined)
                        setShowModal(true)
                    }}
                >
                    <i className="bi bi-plus-circle me-2 d-flex" />
                    Add New Category
                </Button>
            </div>
            <div className="overflow-x-auto">
                <Table
                    handleSearch={handleSearchParams}
                    isCheck={isCheck}
                    columns={columns}
                    handleSortBy={handleSortBy}
                    data={categories}
                />
            </div>
            <Pagination
                size={size}
                totalElements={totalElements}
                setSize={setSize}
                page={page + 1}
                changePage={(newPage) => setPage(newPage)}
            />
            <INameModal
                iNameEntity={currentCategory}
                setEditMode={setEditMode}
                setShowCreateModal={setShowModal}
                setAllList={(list) => setCategories(list as IBlogCategory[])}
                showCreateModal={showModal}
                editMode={editMode}
                page={page + 1}
                size={size}
                setTotalElements={setTotalElements}
                createCall={(data) => createBlogCategory(data as IBlogCategory) as any}
                deleteCall={(id) => deleteBlogCategory(id) as any}
                updateCall={(id, data) => updateBlogCategory(id, data as IBlogCategory) as any}
                getAllCall={(params) => getAllBlogCategories(params) as any}
            />
        </div>
    )
}
